/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// Performance Metrics & Tracing - Lightweight Data Call Instrumentation
// ============================================================================

import { captureException, captureMessage } from "./sentry";
import { logger } from "@/lib/utils/logger";
import type { DataCallMetrics, SLODefinitions } from "./metrics/types";
import { SLO_DEFINITIONS } from "./metrics/slo-definitions";
import {
  calculatePercentiles,
  calculateErrorRate,
  filterMetricsForSLO,
  determineSloKey,
} from "./metrics/slo-helpers";
import { buildSloReport, type SLOReport } from "./metrics/slo-report";

// ============================================================================
// METRICS COLLECTOR
// ============================================================================

class MetricsCollector {
  private metrics: DataCallMetrics[] = [];
  // PERFORMANCE: Aggressively capped in dev to prevent heap bloat (was 500, caused 98% usage)
  private readonly maxMetrics =
    process.env.NODE_ENV === "development" ? 50 : 5000;
  private readonly flushInterval = 60000; // Flush every minute
  private flushTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Start periodic flush in non-test environments
    if (process.env.NODE_ENV !== "test") {
      this.startFlushTimer();
    }
  }

  /**
   * Record a data call metric
   */
  record(metric: Omit<DataCallMetrics, "timestamp">): void {
    const fullMetric: DataCallMetrics = {
      ...metric,
      timestamp: Date.now(),
    };

    // Add to in-memory buffer
    this.metrics.push(fullMetric);

    // Trim if over limit
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }

    // Check SLO violations only in production — dev checks are meaningless
    // and create excessive object allocations (percentile calcs, error rate, etc.)
    if (
      process.env.NODE_ENV !== "development" &&
      (metric.databaseQuery || metric.operation?.includes("api"))
    ) {
      this.checkSLOViolations(fullMetric);
    }
  }

  /**
   * Get metrics for a specific operation type
   */
  getMetrics(operation?: string, windowMs?: number): DataCallMetrics[] {
    const now = Date.now();
    const window = windowMs ?? 5 * 60 * 1000; // Default 5 minutes

    let filtered = this.metrics.filter((m) => now - m.timestamp < window);

    if (operation) {
      filtered = filtered.filter((m) => m.operation === operation);
    }

    return filtered;
  }

  /**
   * Check if metrics violate SLO targets
   */
  private checkSLOViolations(metric: DataCallMetrics): void {
    const sloKey = determineSloKey(metric);
    const slo = SLO_DEFINITIONS[sloKey];

    // Get recent metrics for this operation
    let recentMetrics = this.getMetrics(metric.operation, slo.window);

    // Apply SLO-specific filtering (includes cold start filtering)
    recentMetrics = filterMetricsForSLO(recentMetrics, sloKey);

    // Skip violation checking if we don't have enough samples after filtering
    // Percentiles are unreliable with fewer than 10 samples
    if (recentMetrics.length < 10) {
      return;
    }

    const percentiles = calculatePercentiles(recentMetrics);
    const errorRate = calculateErrorRate(recentMetrics);

    // Check violations
    const violations: string[] = [];

    if (percentiles.p95 > slo.targets.p95) {
      violations.push(
        `p95 latency ${percentiles.p95}ms exceeds target ${slo.targets.p95}ms`,
      );
    }

    if (percentiles.p99 > slo.targets.p99) {
      violations.push(
        `p99 latency ${percentiles.p99}ms exceeds target ${slo.targets.p99}ms`,
      );
    }

    if (errorRate > slo.targets.errorRate) {
      violations.push(
        `Error rate ${(errorRate * 100).toFixed(2)}% exceeds target ${(slo.targets.errorRate * 100).toFixed(2)}%`,
      );
    }

    if (violations.length > 0) {
      logger.warn(`[SLO VIOLATION] ${slo.name}`, {
        violations,
        metrics: {
          p50: percentiles.p50,
          p95: percentiles.p95,
          p99: percentiles.p99,
          errorRate,
          sampleSize: recentMetrics.length,
        },
        operation: metric.operation,
        organizationId: metric.organizationId,
        requestId: metric.requestId,
      });

      // Send to Sentry for critical violations
      if (errorRate > slo.targets.errorRate * 2) {
        void captureException(new Error(`SLO violation: ${slo.name}`), {
          violations,
          metrics: {
            p50: percentiles.p50,
            p95: percentiles.p95,
            p99: percentiles.p99,
            errorRate,
          },
          operation: metric.operation,
        }).catch((err: unknown) => {
          const message = "[METRICS] SLO violation Sentry report failed (non-blocking)";
          logger.warn(message, {
            operation: "METRICS_SLO_SENTRY",
            error: err instanceof Error ? err.message : String(err),
            slo: slo.name,
            metricOperation: metric.operation,
            organizationId: metric.organizationId,
            requestId: metric.requestId,
          });
          void captureMessage(
            message,
            {
              tags: {
                surface: "monitoring",
                operation: "slo_sentry_capture",
                swallow: "intentional_non_blocking",
              },
            },
            "warning",
          );
        });
      }
    }
  }

  /**
   * Get SLO compliance report
   */
  getSLOReport(): SLOReport {
    return buildSloReport((window) => this.getMetrics(undefined, window));
  }

  /**
   * Flush metrics (send to external service, log, etc.)
   */
  private flush(): void {
    if (this.metrics.length === 0) return;

    const report = this.getSLOReport();
    const hasViolations = Object.values(report).some((r) => !r.compliant);

    if (hasViolations) {
      logger.warn("[METRICS] SLO violations detected", { report });
    }

    // In production, you might want to send metrics to an external service
    // For now, we just log and keep in memory
  }

  /**
   * Start periodic flush timer
   * Idempotent: safe to call multiple times, will only start once
   */
  private startFlushTimer(): void {
    // OPTIMIZATION: If already started, don't restart (prevents duplicate intervals)
    if (this.flushTimer) {
      return;
    }

    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  /**
   * Clear all metrics (useful for testing)
   */
  clear(): void {
    this.metrics = [];
  }

  /**
   * Stop flush timer and clean up resources
   */
  stop(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Get current metrics count
   */
  getMetricsCount(): number {
    return this.metrics.length;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const metricsCollector = new MetricsCollector();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Measure execution time of an async function
 */
export async function measureLatency<T>(
  operation: string,
  fn: () => Promise<T>,
  context?: {
    organizationId?: string;
    userId?: string;
    requestId?: string;
    endpoint?: string;
    method?: string;
    cacheHit?: boolean;
    databaseQuery?: boolean;
  },
): Promise<T> {
  const startTime = Date.now();
  let error: unknown = null;
  let result: T | undefined = undefined;

  try {
    result = await fn();
    return result;
  } catch (e) {
    error = e;
    throw e;
  } finally {
    const latency = Date.now() - startTime;
    // result is guaranteed to be assigned if we reach finally without throwing
    const payloadSize = result !== undefined ? estimatePayloadSize(result) : 0;

    metricsCollector.record({
      operation,
      latency,
      errorRate: error ? 1 : 0,
      payloadSize,
      organizationId: context?.organizationId,
      userId: context?.userId,
      requestId: context?.requestId,
      endpoint: context?.endpoint,
      method: context?.method,
      cacheHit: context?.cacheHit,
      databaseQuery: context?.databaseQuery,
    });
  }
}

/**
 * Estimate payload size in bytes.
 * In dev, skip expensive JSON.stringify + Blob creation — just return 0.
 */
function estimatePayloadSize(data: unknown): number {
  if (process.env.NODE_ENV === "development") return 0;
  try {
    if (data === null || data === undefined) return 0;
    if (typeof data === "string") return new Blob([data]).size;
    if (typeof data === "number" || typeof data === "boolean") return 8;
    if (Array.isArray(data)) {
      return data.reduce((sum, item) => sum + estimatePayloadSize(item), 0);
    }
    if (typeof data === "object") {
      const json = JSON.stringify(data);
      return new Blob([json]).size;
    }
    return 0;
  } catch {
    return 0;
  }
}

/**
 * Get SLO definitions for external use
 */
export function getSLODefinitions(): SLODefinitions {
  return SLO_DEFINITIONS;
}

export type {
  DataCallMetrics,
  SLODefinition,
  SLODefinitions,
  SLOTarget,
  SLOReportEntry,
  SLOKey,
} from "./metrics/types";
export type { SLOReport } from "./metrics/slo-report";
export { SLO_DEFINITIONS } from "./metrics/slo-definitions";
